import React from "react";
import { Modal, Button, Badge, Row, Col, ListGroup } from "react-bootstrap";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  FileText,
  ExternalLink,
} from "lucide-react";
import { format } from "date-fns";
import { useAppDispatch, useAppSelector } from "../../../hooks/useRedux";
import { closeModal } from "../../../store/features/ui/uiActions";
import { deleteAppointment } from "../../../store/features/appointments/appointmentsActions";
import { MODAL_TYPES } from "../../../store/features/ui/uiTypes";

const AppointmentDetailsModal = () => {
  const dispatch = useAppDispatch();
  const { modals } = useAppSelector((state) => state.ui);
  const { selectedAppointment } = useAppSelector((state) => state.appointments);
  const { role } = useAppSelector((state) => state.auth);

  const modal = modals[MODAL_TYPES.APPOINTMENT_DETAILS];
  const isOpen = modal?.open || false;
  const appointment = selectedAppointment;

  const handleClose = () => {
    dispatch(closeModal(MODAL_TYPES.APPOINTMENT_DETAILS));
  };

  const handleCancel = () => {
    if (appointment) {
      dispatch(deleteAppointment(appointment.id));
      // dispatch(swal("Appointment canceled successfully"));
      handleClose();
    }
  };

  const handleReschedule = () => {
    if (appointment?.calendlyEventUri) {
      window.open(appointment.calendlyEventUri, "_blank");
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      confirmed: "success",
      pending: "warning",
      canceled: "danger",
    };
    return (
      <Badge bg={variants[status]} className="status-badge">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const canEdit =
    role === "Admin" ||
    (role === "Advisor" &&
      appointment?.attendees.some((a) => a.role === "Advisor"));

  const canCancel = canEdit && appointment?.status !== "canceled";

  if (!appointment) return null;

  return (
    <Modal show={isOpen} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <Calendar size={20} className="me-2" />
          Appointment Details
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row>
          <Col md={8}>
            <div className="mb-3">
              <h5 className="mb-1">{appointment.title}</h5>
              <div className="d-flex align-items-center gap-2 mb-2">
                {getStatusBadge(appointment.status)}
                <small className="text-muted">
                  Created{" "}
                  {format(new Date(appointment.createdAt), "MMM dd, yyyy")}
                </small>
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex align-items-center mb-2">
                <Clock size={16} className="me-2 text-muted" />
                <span>
                  {format(new Date(appointment.start), "MMM dd, yyyy h:mm a")} -
                  {format(new Date(appointment.end), "h:mm a")}
                </span>
              </div>

              {appointment.location && (
                <div className="d-flex align-items-center mb-2">
                  <MapPin size={16} className="me-2 text-muted" />
                  <span>{appointment.location}</span>
                </div>
              )}

              {appointment.description && (
                <div className="d-flex align-items-start">
                  <FileText size={16} className="me-2 text-muted mt-1" />
                  <span>{appointment.description}</span>
                </div>
              )}
            </div>

            <div className="mb-3">
              <h6 className="mb-2">
                <Users size={16} className="me-2" />
                Attendees
              </h6>
              <ListGroup variant="flush">
                {appointment.attendees.map((attendee) => (
                  <ListGroup.Item key={attendee.id} className="px-0 py-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-medium">{attendee.name}</div>
                        <small className="text-muted">{attendee.email}</small>
                      </div>
                      <Badge bg="secondary">{attendee.role}</Badge>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>

            {appointment.dealId && (
              <div className="mb-3">
                <h6 className="mb-2">Associated Deal</h6>
                <Badge bg="info">{appointment.dealId}</Badge>
              </div>
            )}
          </Col>

          <Col md={4}>
            <div className="border rounded p-3">
              <h6 className="mb-3">Actions</h6>
              <div className="d-grid gap-2">
                {appointment.calendlyEventUri && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleReschedule}
                  >
                    <ExternalLink size={14} className="me-1" />
                    Reschedule
                  </Button>
                )}

                {canCancel && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleCancel}
                  >
                    Cancel Appointment
                  </Button>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AppointmentDetailsModal;
